// Two-factor (TOTP) enrolment screen (MFA configuration & enrolment, 2026-10-06).
//
// Rendered by App INSTEAD of the shell when the MFA policy holds an ACTIVE
// session (session/mfaPolicy.js), so the session gate can no longer replace the
// prompt (the old Login-embedded prompt was raced away by ACTIVE).
//
// Steps (Firebase / Identity Platform TOTP):
//   1. verified email — Identity Platform requires it before any MFA enrolment;
//      a real verification email is sent, nothing is marked verified here;
//   2. multiFactor(user).getSession() → TotpMultiFactorGenerator.generateSecret();
//   3. QR rendered LOCALLY from secret.generateQrCodeUrl() (qrcode-generator →
//      data: URL) plus the manual key — the secret never leaves the browser
//      except to Firebase;
//   4. a valid 6-digit code → assertionForEnrollment → multiFactor(user).enroll().
// The secret lives only in component state: never logged, persisted, stored in
// Firestore / localStorage / sessionStorage, or sent to analytics. Cancel /
// unmount drops it. Errors are logged by code only.
import React, { useState, useEffect, useCallback } from 'react';
import { multiFactor, TotpMultiFactorGenerator, sendEmailVerification } from 'firebase/auth';
import qrcode from 'qrcode-generator';
import { reauthenticateWithPassword } from '../services/authService';
import { MFA } from '../session/mfaPolicy';

const ISSUER = 'FirmAssist';

export function qrDataUrlFor(uri) {
  const qr = qrcode(0, 'M');
  qr.addData(uri);
  qr.make();
  return qr.createDataURL(5, 2);
}

const wrap = { minHeight: '100vh', background: '#080a10', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', boxSizing: 'border-box', fontFamily: "'DM Sans', sans-serif" };
const card = { maxWidth: '460px', width: '100%', background: '#0f1219', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '2rem', color: '#a8adb7' };
const h2 = { color: '#e8eaed', margin: '0 0 0.5rem', fontSize: '1.25rem' };
const p = { lineHeight: 1.6, margin: '0 0 1rem', fontSize: '0.9rem' };
const primary = { width: '100%', padding: '0.8rem', border: 'none', borderRadius: '10px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: '0.6rem' };
const secondary = { width: '100%', padding: '0.6rem', background: 'none', border: 'none', color: '#8b8fa3', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' };
const input = { width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.3)', color: '#e8eaed', fontSize: '1.1rem', letterSpacing: '0.3rem', textAlign: 'center', boxSizing: 'border-box', marginBottom: '0.75rem', fontFamily: 'inherit' };
const errBox = { padding: '0.75rem', borderRadius: '10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem' };

const MfaEnrollment = ({ user, requirement, onEnrolled, onDefer, onSignOut }) => {
  const [step, setStep] = useState(user && user.emailVerified ? 'intro' : 'verify-email');
  const [secret, setSecret] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const canDefer = requirement !== MFA.REQUIRED;

  // Drop the secret whenever this screen goes away.
  useEffect(() => () => { setSecret(null); setQrUrl(''); }, []);

  const clearSecret = () => { setSecret(null); setQrUrl(''); setCode(''); };

  const handleError = useCallback((err, fallback) => {
    const c = err && err.code;
    console.warn('[MFA] enrolment step failed:', c || 'unknown'); // code only — never the secret
    if (c === 'auth/unverified-email') { clearSecret(); setStep('verify-email'); setError('Please verify your email address first.'); return; }
    if (c === 'auth/requires-recent-login') { clearSecret(); setStep('reauth'); setError('For your security, please confirm your password to continue.'); return; }
    if (c === 'auth/invalid-verification-code') { setError('That code is not correct. Enter the current 6-digit code from your authenticator app.'); return; }
    if (c === 'auth/too-many-requests') { setError('Too many attempts. Please wait a few minutes and try again.'); return; }
    if (c === 'auth/operation-not-allowed') { clearSecret(); setStep('unavailable'); setError(''); return; }
    setError(fallback);
  }, []);

  const sendVerification = async () => {
    setBusy(true); setError(''); setNotice('');
    try { await sendEmailVerification(user); setNotice('Verification email sent. Open the link in that email, then come back and continue.'); }
    catch (e) { handleError(e, 'We could not send the verification email. Please try again.'); }
    setBusy(false);
  };

  const confirmVerified = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await user.reload();
      if (user.emailVerified) { await user.getIdToken(true); setStep('intro'); }
      else setError('Your email is not verified yet. Open the link in the verification email, then try again.');
    } catch (e) { handleError(e, 'We could not check your verification status. Please try again.'); }
    setBusy(false);
  };

  const startEnrolment = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const session = await multiFactor(user).getSession();
      const s = await TotpMultiFactorGenerator.generateSecret(session);
      setSecret(s);
      setQrUrl(qrDataUrlFor(s.generateQrCodeUrl(user.email || 'FirmAssist account', ISSUER)));
      setStep('scan');
    } catch (e) { handleError(e, 'We could not start two-factor setup. Please try again.'); }
    setBusy(false);
  };

  const confirmCode = async () => {
    if (!secret || code.length !== 6) return;
    setBusy(true); setError('');
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      clearSecret();
      setBusy(false);
      onEnrolled();
      return;
    } catch (e) { handleError(e, 'Two-factor setup could not be completed. Please try again.'); }
    setBusy(false);
  };

  const reauth = async () => {
    setBusy(true); setError('');
    const r = await reauthenticateWithPassword(password);
    setPassword('');
    setBusy(false);
    if (r && r.success) { setStep('intro'); return; }
    setError((r && r.error) || 'We could not confirm your password.');
  };

  const cancelScan = () => { clearSecret(); setError(''); setStep('intro'); };

  return (
    <div style={wrap} role="main" aria-label="Two-factor authentication setup">
      <div style={card}>
        <h2 style={h2}>Secure your account</h2>
        <p style={p}>
          {requirement === MFA.REQUIRED
            ? 'Two-factor authentication is required for your account before you can continue.'
            : 'As a legal professional handling privileged data, please protect your account with two-factor authentication.'}
        </p>
        {error && <div role="alert" style={errBox}>{error}</div>}
        {notice && <div role="status" style={{ ...p, color: '#10b981' }}>{notice}</div>}

        {step === 'verify-email' && (
          <>
            <p style={p}>Two-factor setup needs a verified email address. We'll send a verification link to your account email.</p>
            <button type="button" style={primary} disabled={busy} onClick={sendVerification}>Send verification email</button>
            <button type="button" style={primary} disabled={busy} onClick={confirmVerified}>I've verified my email</button>
          </>
        )}

        {step === 'intro' && (
          <>
            <p style={p}>You'll need an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.</p>
            <button type="button" style={primary} disabled={busy} onClick={startEnrolment}>Set up authenticator app</button>
          </>
        )}

        {step === 'reauth' && (
          <>
            <input type="password" aria-label="Current password" placeholder="Current password" autoComplete="current-password" style={{ ...input, letterSpacing: 'normal', textAlign: 'left' }} value={password} onChange={(e) => setPassword(e.target.value)} />
            <button type="button" style={primary} disabled={busy || !password} onClick={reauth}>Confirm password</button>
          </>
        )}

        {step === 'scan' && secret && (
          <>
            <p style={p}>1. Scan this QR code with your authenticator app.</p>
            {qrUrl && <img src={qrUrl} alt="Authenticator setup QR code" style={{ display: 'block', margin: '0 auto 1rem', background: '#fff', padding: '8px', borderRadius: '8px' }} />}
            <p style={{ ...p, fontSize: '0.8rem' }}>Can't scan? Enter this key manually:</p>
            <div data-testid="totp-manual-key" style={{ padding: '0.6rem', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', fontFamily: "'Courier New', monospace", color: '#d4a853', wordBreak: 'break-all', userSelect: 'all', marginBottom: '1rem' }}>{secret.secretKey}</div>
            <p style={p}>2. Enter the 6-digit code shown in the app:</p>
            <input aria-label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" style={input} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmCode(); }} />
            <button type="button" style={primary} disabled={busy || code.length !== 6} onClick={confirmCode}>Enable two-factor authentication</button>
            <button type="button" style={secondary} disabled={busy} onClick={cancelScan}>Cancel</button>
          </>
        )}

        {step === 'unavailable' && (
          <p style={p}>Two-factor setup isn't available right now. Please try again later or contact FirmAssist support.</p>
        )}

        {(canDefer || step === 'unavailable') && <button type="button" style={secondary} disabled={busy} onClick={() => { clearSecret(); onDefer(); }}>Remind me later</button>}
        <button type="button" style={secondary} disabled={busy} onClick={() => { clearSecret(); onSignOut(); }}>Sign out</button>
      </div>
    </div>
  );
};

export default MfaEnrollment;
